import React, { useEffect, useRef, useState } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import {
  AuthenticationErrorTypes,
  LoginErrorMessage,
} from '~/login/utils/LoginErrorMessages';
import InputFields from '~/shared/components/InputFields';
import featureNames from '~/shared/constants/featureFlags';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useAccountActions } from '~/techstyle-shared/react-accounts';
import {
  Button,
  PasswordField,
  mobile,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { PinVerificationFormTitle } from '../PinVerificationForm/PinVerificationForm';
import PinVerificationMethods from '../PinVerificationMethods/PinVerificationMethods';

const StyledPasswordField = styled(PasswordField)`
  fieldset {
    border-bottom: 1px solid transparent;
  }
`;

const actionButtonMobileStyles = css`
  height: 52px;
  width: 100%;
`;

const LoginButton = styled(Button).attrs({
  variant: 'primary',
})`
  padding-top: 12px;
  padding-bottom: 12px;
  width: 100%;
  min-width: 270px;
  ${mobile`
    ${actionButtonMobileStyles}
    margin-bottom: 12px;
  `}
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ForgotPasswordContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const ForgotPasswordControlButton = styled(Button).attrs({
  variant: 'unstyled',
})`
  padding: 0;
  align-self: flex-end;
  ${({ theme }) => theme.loginForm.forgotPassword};
  color: ${({ theme }) => theme.colors.loginAuthForgotPasswordControlButton};
`;

const BackButtonContainer = styled.div`
  position: absolute;
  top: 14px;
  left: 12px;
  ${mobile`
    top: 34px;
  `}
`;

const AuthLayoutContainer = styled.div`
  min-width: 315px;
  max-width: 500px;
`;

const TurnstileContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.sizes.sm}px;
`;

function AuthLayout({
  setForgotPassword,
  email,
  onVerificationMethodSelect,
  handlePasswordSubmit,
  loginError,
  isSubmitting,
  password,
  setPassword,
  turnstileWidgetIdRef,
}) {
  const [isValid, setIsValid] = useState(false);

  const [maskedContactInfo, setMaskedContactInfo] = useState({
    email: email ? `${email.slice(0, 3)}***@***` : null,
    phone: null,
  });

  const [loginMethods, setLoginMethods] = useState({
    password: true,
    sms: false,
    email: false,
    captcha: false,
    confirm: '',
  });
  const [loginMethodsFetched, setLoginMethodsFetched] = useState(false);

  const actions = useAccountActions();

  const { enabled: isTurnstileEnabled } = useHasFeature(featureNames.TURNSTILE);
  const turnstileSiteKey = config.get('public.recaptcha.siteKey');
  const turnstileRef = useRef(null);

  useEffect(() => {
    let interval;
    let timeout;
    const TURNSTILE_SCRIPT_TIMEOUT_MS = 10000;

    if (
      isTurnstileEnabled &&
      loginMethodsFetched &&
      loginMethods.captcha &&
      turnstileRef.current &&
      turnstileWidgetIdRef.current === null
    ) {
      const renderWidget = () => {
        if (window.turnstile && turnstileRef.current) {
          turnstileWidgetIdRef.current = window.turnstile.render(
            turnstileRef.current,
            {
              sitekey: turnstileSiteKey,
              theme: 'light',
              size: 'normal',
            }
          );
        }
      };

      if (window.turnstile) {
        renderWidget();
      } else {
        interval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(interval);
            clearTimeout(timeout);
            renderWidget();
          }
        }, 100);
        timeout = setTimeout(() => {
          clearInterval(interval);
        }, TURNSTILE_SCRIPT_TIMEOUT_MS);
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      if (timeout) {
        clearTimeout(timeout);
      }
      if (window.turnstile && turnstileWidgetIdRef.current != null) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [
    loginMethodsFetched,
    loginMethods.captcha,
    turnstileSiteKey,
    turnstileWidgetIdRef,
    isTurnstileEnabled,
  ]);

  useEffect(() => {
    async function fetchLoginMethods() {
      try {
        if (email) {
          const response = await actions.loginMethodsFromEmail(email);
          const loginMethods = {
            password: response?.payload?.password,
            sms: response?.payload?.sms,
            email: response?.payload?.email,
            captcha: response?.payload?.captcha,
            confirm: response?.payload?.confirm,
          };
          setLoginMethods(loginMethods);
          setMaskedContactInfo({
            email: email ? `${email.slice(0, 3)}***@***` : null,
            phone: loginMethods?.confirm
              ? `XXX-XXX-${loginMethods?.confirm}`
              : null,
          });
        }
      } catch (error) {
        // Do nothing.
      } finally {
        setLoginMethodsFetched(true);
      }
    }

    fetchLoginMethods();
  }, [actions, email, setLoginMethods, setMaskedContactInfo]);

  useEffect(() => {
    if (password.length >= 6) {
      setIsValid(true);
    }
  }, [password]);

  if (!email) {
    return null;
  }

  if (!loginMethods?.password) {
    loginError = AuthenticationErrorTypes.BLOCKED_LOGIN;
  }

  return (
    <AuthLayoutContainer>
      <PinVerificationFormTitle>
        <FormattedMessage
          id="site_login.identity_verification_modal_heading"
          defaultMessage="Verify Your Identity"
        />
      </PinVerificationFormTitle>

      <PinVerificationMethods
        email={email}
        loginMethods={loginMethods}
        maskedContactInfo={maskedContactInfo}
        onVerificationMethodSelect={onVerificationMethodSelect}
        showVerifyIdentityTitle
      />

      <>
        {loginError !== AuthenticationErrorTypes.BLOCKED_LOGIN && (
          <InputFields>
            <StyledPasswordField
              name="password"
              variant="standard"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              required
              label={
                <FormattedMessage
                  id="global_cta.password"
                  defaultMessage="Password *"
                />
              }
              data-autotag="login_page_password"
              error={loginError === AuthenticationErrorTypes.LOGIN_FAILED}
            />
          </InputFields>
        )}
        {loginError && <LoginErrorMessage error={loginError} />}

        <ForgotPasswordContainer>
          <ForgotPasswordControlButton
            data-autotag="forgot_password_link"
            onClick={setForgotPassword}
          >
            <FormattedMessage
              id="site_login.forgot_password1"
              defaultMessage="Forgot your password?"
            />
          </ForgotPasswordControlButton>
        </ForgotPasswordContainer>

        {loginError !== AuthenticationErrorTypes.BLOCKED_LOGIN &&
          isTurnstileEnabled &&
          loginMethodsFetched &&
          loginMethods.captcha && <TurnstileContainer ref={turnstileRef} />}

        {loginError !== AuthenticationErrorTypes.BLOCKED_LOGIN && (
          <Actions>
            <LoginButton
              type="submit"
              disabled={isSubmitting || !isValid}
              isLoading={isSubmitting}
              data-autotag="login_page_login_btn"
              onClick={handlePasswordSubmit}
            >
              <FormattedMessage
                id="site_login.Sign_In"
                defaultMessage="Sign In"
              />
            </LoginButton>
          </Actions>
        )}
      </>
    </AuthLayoutContainer>
  );
}

AuthLayout.propTypes = {
  email: PropTypes.string,
  handlePasswordSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  loginError: PropTypes.object,
  onVerificationMethodSelect: PropTypes.func,
  password: PropTypes.string,
  setForgotPassword: PropTypes.func,
  setPassword: PropTypes.func,
  turnstileWidgetIdRef: PropTypes.object,
};

AuthLayout.BackButtonContainer = BackButtonContainer;

export default AuthLayout;
